import PdfViewIcon from '@listening/assets/icons/pdfView.svg?react';
import ShareIcon from '@listening/assets/icons/share2.svg?react';
import TextViewIcon from '@listening/assets/icons/textView.svg?react';
import LogoBig from '@listening/assets/imgs/logo/listeningLogoLong.svg?react';
import LogoBigDark from '@listening/assets/imgs/logo/listeningLogoLongDark.svg?react';
import LogoSmall from '@listening/assets/imgs/logo/logoSmall.svg?react';
import { cn } from '@listening/utils/react-helpers';
import { DialogDropdown } from '@listening/frontend/components/custom/dialog-dropdown';
import { Switch } from '@listening/frontend/components/radix/switch';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@listening/frontend/components/radix/toggle-group';
import { TooltipButton } from '@listening/frontend/components/custom/tooltip-button';
import {
  isReadAlongPdfAvailable,
  useAudioPrefsStore,
} from '@listening/frontend/stores/audio-preferences-store';
import { Link } from '@tanstack/react-router';
import { Button } from '@listening/frontend/components/radix/button';
import {
  Compress,
  Download,
  Expand,
  Minus,
  MoreVert,
  Plus,
} from 'iconoir-react';
import React, { use, useEffect, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import {
  CtaButton,
  CtaButtonContext,
} from '@/components/astro/cta-button/cta-button';
import type { EdgeBucketedFlagKey } from '@listening/frontend/analytics/define-route-analytics';
import { useTheme } from '@/components/shared-ui/theme-provider';
import { ThemeToggler } from '@/components/shared-ui/theme-toggler';
import { TextViewSettingsPanel } from '@/components/shared-ui/text-view-settings-panel';
import * as m from '@/paraglide/messages';
import { localizeHref } from '@/paraglide/runtime';
import { audioService } from '@/services/audio-service';
import { useReadAlongViewer } from '@/components/start/read-along/read-along-store';
import { DocsExperimentSelector } from './docs-experiment-selector';
import { useCopyText } from '@/lib/clipboard';

function useScrollDirection() {
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    let lastScrollTop = 0;
    let ticking = false;

    const updateScrollDir = (scrollTop: number) => {
      const diff = scrollTop - lastScrollTop;

      if (scrollTop < 50) {
        setIsHidden(false);
      } else if (diff > 8) {
        setIsHidden(true);
      } else if (diff < -8) {
        setIsHidden(false);
      }

      lastScrollTop = scrollTop;
      ticking = false;
    };

    const onScroll = (e: Event) => {
      if (ticking) return;

      const target = e.target as HTMLElement | Document;
      let scrollTop = 0;

      if (target === document || target === document.documentElement) {
        scrollTop = window.scrollY || document.documentElement.scrollTop;
      } else if (target instanceof HTMLElement) {
        scrollTop = target.scrollTop;
      }

      window.requestAnimationFrame(() => updateScrollDir(scrollTop));
      ticking = true;
    };

    document.addEventListener('scroll', onScroll, {
      passive: true,
      capture: true,
    });
    return () =>
      document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  return isHidden;
}

export function DocsTopBar({
  className,
  experimentNames,
  keepMobileHeaderVisible = false,
  mobileReaderControlsEnabled = true,
  showDownload = true,
}: {
  className?: string | undefined;
  experimentNames?: ReadonlyArray<EdgeBucketedFlagKey> | undefined;
  keepMobileHeaderVisible?: boolean;
  mobileReaderControlsEnabled?: boolean;
  showDownload?: boolean;
}) {
  const audioItem = audioService.store.useCurrentAudio().query.data;
  return (
    <DocsTopBarContent
      className={className}
      experimentNames={experimentNames}
      keepMobileHeaderVisible={keepMobileHeaderVisible}
      mobileReaderControlsEnabled={mobileReaderControlsEnabled}
      showDownload={showDownload}
      readAlongPdfAvailable={isReadAlongPdfAvailable(audioItem)}
    />
  );
}

function DocsTopBarContent({
  className,
  experimentNames,
  keepMobileHeaderVisible,
  mobileReaderControlsEnabled,
  showDownload,
  readAlongPdfAvailable,
}: {
  className?: string | undefined;
  experimentNames?: ReadonlyArray<EdgeBucketedFlagKey> | undefined;
  keepMobileHeaderVisible: boolean;
  mobileReaderControlsEnabled: boolean;
  showDownload: boolean;
  readAlongPdfAvailable: ReturnType<typeof isReadAlongPdfAvailable>;
}) {
  const { isLight, theme, setThemePref } = useTheme();
  const Logo = isLight ? LogoBig : LogoBigDark;
  const { value: readAlongViewer, set: setReadAlongViewer } =
    useReadAlongViewer();
  const docFullscreen = useAudioPrefsStore((s) => s.docFullscreen);
  const setDocFullscreen = useAudioPrefsStore((s) => s.setDocFullscreen);
  const hasPdf = readAlongPdfAvailable.isPdf;
  const activeReadAlongViewer = hasPdf ? readAlongViewer : 'normal';
  const isScrollHidden = useScrollDirection();
  const isMobileScreen = useMediaQuery('(max-width: 639px)');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const shouldHideNavbar =
    !keepMobileHeaderVisible &&
    isMobileScreen &&
    isScrollHidden &&
    !isDropdownOpen;
  // Spread what we inherit: this provider only means to set a label. See the
  // invariant on `CtaButtonContext` — replacing the value outright would opt
  // this bar out of any page-level `href` retarget.
  const inheritedCta = use(CtaButtonContext);

  return (
    <CtaButtonContext
      value={{
        ...inheritedCta,
        label: m.common_try_now(),
      }}
    >
      <section
        className={cn(
          'flex w-full min-w-0 items-center justify-between border-b border-[#E5E7EB] bg-white px-3 transition-all duration-300 sm:px-7 sm:py-3 dark:border-slate-700 dark:bg-[#0a0e1a]',
          shouldHideNavbar ? 'h-0 overflow-hidden border-b-0 py-0' : 'py-3',
          className,
        )}
      >
        <div className='flex min-w-0 shrink-0 items-center gap-4'>
          <Link
            to={localizeHref('/')}
            className='flex w-8 shrink-0 items-center sm:w-[100px]'
            aria-label={`${m.nav_brand_name()} home`}
          >
            <LogoSmall className='size-7 sm:hidden' />
            <Logo className='hidden object-scale-down sm:block' />
          </Link>
        </div>

        <div className='flex min-w-0 shrink-0 items-center gap-2 sm:gap-3'>
          {mobileReaderControlsEnabled && (
            <TooltipButton
              toolTipContent={
                docFullscreen
                  ? m.read_along_exit_fullscreen()
                  : m.read_along_fullscreen()
              }
              variant='ghost'
              className='h-auto p-2 text-muted-foreground sm:hidden'
              onClick={() => setDocFullscreen(!docFullscreen)}
            >
              {docFullscreen ? (
                <Compress className='size-5' />
              ) : (
                <Expand className='size-5' />
              )}
            </TooltipButton>
          )}
          {hasPdf && (
            <div
              className='hidden shrink-0 lg:block'
              data-tour-id='view-switcher'
            >
              <ContentSwitcher />
            </div>
          )}
          {showDownload && hasPdf && (
            <div className='hidden lg:block' data-tour-id='download'>
              <DownloadButton documentUrl={readAlongPdfAvailable.documentUrl} />
            </div>
          )}
          {hasPdf && readAlongViewer === 'pdf' && (
            <div className='hidden xl:block'>
              <PdfScaleControls />
            </div>
          )}
          <div className='hidden sm:block'>
            <ShareButton />
          </div>
          <div className='hidden sm:block'>
            <ThemeToggler />
          </div>
          {experimentNames && (
            <DocsExperimentSelector
              experimentNames={experimentNames}
              onOpenChange={setIsDropdownOpen}
            />
          )}
          <div data-tour-id='more-menu'>
            <DocsSettingsDropdown
              readAlongViewer={activeReadAlongViewer}
              setReadAlongViewer={hasPdf ? setReadAlongViewer : undefined}
              mobileReaderControlsEnabled={mobileReaderControlsEnabled}
              downloadUrl={
                showDownload && hasPdf
                  ? readAlongPdfAvailable.documentUrl
                  : undefined
              }
              theme={theme}
              setThemePref={setThemePref}
              onOpenChange={setIsDropdownOpen}
            />
          </div>
          <CtaButton className='inline-flex rounded-full bg-[#1a1a2e] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#1a1a2e]/80 sm:px-4 sm:py-2 sm:text-sm dark:bg-white dark:text-black dark:hover:bg-white/80' />
        </div>
      </section>
    </CtaButtonContext>
  );
}

function DocsSettingsDropdown({
  readAlongViewer,
  setReadAlongViewer,
  downloadUrl,
  mobileReaderControlsEnabled,
  theme,
  setThemePref,
  onOpenChange,
}: {
  readAlongViewer: 'normal' | 'pdf';
  setReadAlongViewer?: ((v: 'normal' | 'pdf') => void) | undefined;
  downloadUrl?: string | undefined;
  mobileReaderControlsEnabled: boolean;
  theme: string;
  setThemePref: (v: 'light' | 'dark') => void;
  onOpenChange?: (open: boolean) => void;
}) {
  const { copy } = useCopyText();
  return (
    <DialogDropdown
      align='end'
      className='max-h-[80vh] w-[min(380px,calc(100vw-1rem))] overflow-y-auto rounded-[14px] border border-[#e3ebf7] bg-white shadow-[0_1px_2px_rgba(11,31,58,0.04),0_8px_24px_rgba(37,99,235,0.10),0_24px_60px_rgba(11,31,58,0.08)] dark:border-white/10 dark:bg-slate-900 dark:shadow-2xl'
      {...(onOpenChange && { onOpenChange })}
      trigger={({ ref }) => (
        <TooltipButton
          ref={ref as React.RefObject<HTMLButtonElement | null>}
          toolTipContent={m.read_along_more_options()}
          variant='ghost'
          className='h-auto p-2 text-muted-foreground'
        >
          <MoreVert className='size-5' />
        </TooltipButton>
      )}
    >
      {setReadAlongViewer && mobileReaderControlsEnabled && (
        <div className='px-[18px] pt-3.5 sm:border-b sm:border-[#e3ebf7] sm:pb-3 lg:hidden sm:dark:border-white/10'>
          <span className='mb-2.5 block text-[10px] font-semibold tracking-[0.14em] text-[#5b6f8e] uppercase dark:text-slate-400'>
            {m.read_along_view()}
          </span>
          <ToggleGroup
            className='w-full gap-1 rounded-lg bg-[#eef3fc] p-1 dark:bg-white/5'
            type='single'
            value={readAlongViewer}
            onValueChange={(val) => {
              if (val) setReadAlongViewer(val as 'normal' | 'pdf');
            }}
          >
            <ToggleGroupItem
              className='flex flex-1 gap-1.5 rounded-md text-xs data-[state=on]:bg-white data-[state=on]:shadow-sm dark:data-[state=on]:bg-white/15'
              value='normal'
            >
              <TextViewIcon className='size-3.5' />
              {m.common_text()}
            </ToggleGroupItem>
            <ToggleGroupItem
              className='flex flex-1 gap-1.5 rounded-md text-xs data-[state=on]:bg-white data-[state=on]:shadow-sm dark:data-[state=on]:bg-white/15'
              value='pdf'
            >
              <PdfViewIcon className='size-3.5' />
              PDF
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      )}

      <div className={cn(!mobileReaderControlsEnabled && 'max-lg:hidden')}>
        <TextViewSettingsPanel
          mode={readAlongViewer === 'pdf' ? 'pdf' : 'text'}
        />
      </div>

      <div
        className={cn(
          'px-[18px] py-2 sm:hidden',
          'border-t border-[#e3ebf7] dark:border-white/10',
        )}
      >
        {mobileReaderControlsEnabled &&
          setReadAlongViewer &&
          readAlongViewer === 'pdf' && (
            <div className='mb-2 xl:hidden'>
              <span className='mb-2.5 block text-[10px] font-semibold tracking-[0.14em] text-[#5b6f8e] uppercase dark:text-slate-400'>
                {m.read_along_zoom()}
              </span>
              <PdfScaleControls fullWidth />
            </div>
          )}

        <label className='flex cursor-pointer items-center justify-between rounded-md py-2'>
          <span className='text-[13px] font-medium text-[#1a2c4e] dark:text-slate-200'>
            {theme === 'light' ? m.nav_app_dark_mode() : m.nav_app_light_mode()}
          </span>
          <Switch
            checked={theme === 'dark'}
            onCheckedChange={(checked) =>
              setThemePref(checked ? 'dark' : 'light')
            }
          />
        </label>

        <button
          type='button'
          className='flex w-full items-center gap-3 rounded-md py-2 text-[13px] font-medium text-[#1a2c4e] dark:text-slate-200'
          onClick={() => {
            void copy(window.location.href);
          }}
        >
          <ShareIcon className='size-4' />
          {m.read_along_copy_link()}
        </button>

        {downloadUrl && (
          <DownloadButton documentUrl={downloadUrl} variant='menu' />
        )}
      </div>

      {setReadAlongViewer && readAlongViewer === 'pdf' && (
        <div className='hidden border-t border-[#e3ebf7] px-[18px] py-2 sm:block xl:hidden dark:border-white/10'>
          <span className='mb-2.5 block text-[10px] font-semibold tracking-[0.14em] text-[#5b6f8e] uppercase dark:text-slate-400'>
            {m.read_along_zoom()}
          </span>
          <PdfScaleControls fullWidth />
        </div>
      )}

      {downloadUrl && (
        <div className='hidden px-[18px] py-2 sm:block lg:hidden'>
          <DownloadButton documentUrl={downloadUrl} variant='menu' />
        </div>
      )}
    </DialogDropdown>
  );
}

function DownloadButton({
  documentUrl,
  variant = 'icon',
}: {
  documentUrl: string;
  variant?: 'icon' | 'menu';
}) {
  if (variant === 'menu') {
    return (
      <a
        href={documentUrl}
        download
        target='_blank'
        rel='noreferrer'
        className='flex w-full items-center gap-3 rounded-md py-2 text-[13px] font-medium text-[#1a2c4e] dark:text-slate-200'
      >
        <Download className='size-4' />
        {m.common_download()}
      </a>
    );
  }

  return (
    <Button
      asChild
      variant='ghost'
      className='h-11 gap-2 rounded-full bg-[#eef0ff] px-4 text-base font-medium text-[#526cff] hover:bg-[#e4e7ff] hover:text-[#4058f4] xl:px-5 dark:bg-[#1e2a5a] dark:text-[#9eb0ff] dark:hover:bg-[#26366f] dark:hover:text-[#bdc8ff]'
    >
      <a href={documentUrl} download target='_blank' rel='noreferrer'>
        <Download aria-hidden='true' className='size-5' />
        {m.common_download()} PDF
      </a>
    </Button>
  );
}

function ShareButton() {
  const { copy, copied } = useCopyText();

  return (
    <TooltipButton
      toolTipContent={copied ? m.dialog_copied() : m.read_along_copy_link()}
      variant='ghost'
      className='h-auto p-2 text-muted-foreground'
      onClick={() => {
        void copy(window.location.href);
      }}
    >
      {copied ? (
        <span className='size-5 text-green-600'>✓</span>
      ) : (
        <ShareIcon className='size-5' />
      )}
    </TooltipButton>
  );
}

function ContentSwitcher({ small }: { small?: boolean }) {
  const { value: readAlongViewer, set: setReadAlongViewer } =
    useReadAlongViewer();

  return (
    <ToggleGroup
      className={cn(
        'shrink-0 gap-1 rounded-2xl bg-[#F3F4F6] dark:bg-slate-800',
        small ? 'p-1' : 'p-1.5',
      )}
      type='single'
      value={readAlongViewer}
      onValueChange={(val) => {
        if (val) setReadAlongViewer(val as 'normal' | 'pdf');
      }}
    >
      <ToggleGroupItem
        className={cn(
          'shrink-0 rounded-xl p-0 data-[state=on]:bg-white data-[state=on]:shadow-sm dark:data-[state=on]:bg-slate-700',
          small ? 'size-8' : 'size-9 sm:size-10',
        )}
        value='normal'
        aria-label={m.read_along_text_view()}
      >
        <TextViewIcon className={small ? 'size-4' : 'size-5'} />
      </ToggleGroupItem>
      <ToggleGroupItem
        className={cn(
          'shrink-0 rounded-xl p-0 data-[state=on]:bg-white data-[state=on]:shadow-sm dark:data-[state=on]:bg-slate-700',
          small ? 'size-8' : 'size-9 sm:size-10',
        )}
        value='pdf'
        aria-label={m.read_along_pdf_view()}
      >
        <PdfViewIcon className={small ? 'size-4' : 'size-5'} />
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

function PdfScaleControls({ fullWidth }: { fullWidth?: boolean }) {
  const setPdfScale = useAudioPrefsStore((state) => state.setPdfScale);
  const rawScale = useAudioPrefsStore((state) => state.pdfScale);

  return (
    <div
      className={cn(
        'grid h-11 grid-cols-[2.25rem_1fr_2.25rem] items-center rounded-full border border-[#E5E7EB] dark:border-slate-700',
        fullWidth ? 'w-full' : 'w-[136px]',
      )}
    >
      <Button
        aria-label={m.read_along_zoom_out()}
        variant='ghost'
        className='h-11 w-9 rounded-l-full p-0'
        disabled={rawScale <= 0.2}
        onClick={() => setPdfScale(rawScale - 0.1)}
      >
        <Minus className='size-4' />
      </Button>
      <span className='text-center text-base text-muted-foreground'>
        {Math.round(rawScale * 100)}%
      </span>
      <Button
        aria-label={m.read_along_zoom_in()}
        variant='ghost'
        className='h-11 w-9 rounded-r-full p-0'
        disabled={rawScale >= 5}
        onClick={() => setPdfScale(rawScale + 0.1)}
      >
        <Plus className='size-4' />
      </Button>
    </div>
  );
}
